import React from 'react';
import { motion } from 'framer-motion';
import { Copy, Play, Download } from 'lucide-react';
import { ChatMessage } from '../../stores/useChatStore.js';
import { PluginCard } from './PluginCard.js';
import { openInContextCanvas, useCanvasContextStore } from '../../studio/canvas';
import { RunLogStatement } from './RunLogStatement.js';
import { copyMessageToClipboard } from '../../utils/copyMessageContent.js';

interface MessageCardProps {
    message: ChatMessage;
    colors: {
        iconBg: string;
        messageBg: string;
        border: string;
    };
}

const MessageCard: React.FC<MessageCardProps> = ({ message, colors }) => {
    // CQC-F-022: Use shared copy helper that handles artifact fallback chain
    const handleCopy = () => {
        copyMessageToClipboard(message);
    };

    const handleActionClick = (
        action: { label?: string } | string,
        pluginData: { data?: { url?: string; title?: string } }
    ) => {
        const actionName = typeof action === 'string' ? action : action.label || '';

        if (actionName === 'Retry' && message.relatedTaskId) {
            window.dispatchEvent(
                new CustomEvent('beehive:retry', {
                    detail: { taskId: message.relatedTaskId },
                })
            );
        } else if (actionName === 'Recall' && message.relatedTaskId) {
            window.dispatchEvent(
                new CustomEvent('beehive:recall', {
                    detail: { taskId: message.relatedTaskId },
                })
            );
        } else if (actionName === 'View PR' && pluginData.data?.url) {
            // Context Canvas v1: Route PR links to center panel, fall back to new tab if canvas not mounted
            const routed = openInContextCanvas({
                type: 'browser',
                url: pluginData.data.url,
                title: pluginData.data.title || 'Pull Request',
            });
            if (!routed) {
                window.open(pluginData.data.url, '_blank', 'noopener,noreferrer');
            }
        } else if (actionName === 'View App' && pluginData.data?.url) {
            // Context Canvas v1: Route deploy/app links to center panel, fall back to new tab if canvas not mounted
            const routed = openInContextCanvas({
                type: 'browser',
                url: pluginData.data.url,
                title: pluginData.data.title || 'Application',
            });
            if (!routed) {
                window.open(pluginData.data.url, '_blank', 'noopener,noreferrer');
            }
        } else if (actionName === 'More Details' && pluginData.data) {
            // Context Canvas v1: Route "More Details" to detail view in center panel
            // No fallback needed - detail view only makes sense in canvas context
            openInContextCanvas({
                type: 'detail',
                title: pluginData.data.title || 'Details',
                artifact: {
                    type: 'generic',
                    data: pluginData.data as Record<string, unknown>,
                },
            });
        }
    };

    const renderCodeMessage = () => (
        <div className={`border ${colors.border} rounded-lg overflow-hidden`}>
            <div className="flex items-center justify-between p-2 glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-b border-slate-700/30">
                <span className="text-xs font-mono text-slate-400">Code Block</span>
                <div className="flex items-center space-x-1">
                    <motion.button
                        onClick={handleCopy}
                        className="p-1 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/50"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Copy className="w-3 h-3" />
                    </motion.button>
                    <motion.button
                        className="p-1 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/50"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Play className="w-3 h-3" />
                    </motion.button>
                </div>
            </div>
            <div className="p-3 bg-slate-900/50 overflow-hidden">
                <pre className="text-base font-mono text-slate-200 whitespace-pre-wrap break-words leading-relaxed max-w-full">
                    <code className="break-words">{message.content}</code>
                </pre>
            </div>
        </div>
    );

    const renderPluginCard = () => {
        let pluginData;
        try {
            pluginData = JSON.parse(message.content);
        } catch {
            pluginData = { title: 'Plugin Result', description: message.content };
        }

        // Context Canvas v1: Hide "More Details" action when canvas isn't mounted
        // This prevents stranded users in non-Studio contexts where detail view doesn't exist
        const isCanvasMounted = useCanvasContextStore.getState().isCanvasMounted;
        const filteredActions = pluginData.actions?.filter(
            (action: string | { label?: string }) => {
                const actionName = typeof action === 'string' ? action : action.label || '';
                // Hide "More Details" when canvas isn't mounted (case-insensitive)
                if (actionName.toLowerCase() === 'more details' && !isCanvasMounted) {
                    return false;
                }
                return true;
            }
        );

        return (
            <PluginCard
                data={{
                    title: pluginData.title || 'Plugin Result',
                    description: pluginData.description || message.content,
                    status: pluginData.status,
                    data: pluginData.data,
                    actions: filteredActions,
                }}
                onAction={action => {
                    handleActionClick({ label: action }, pluginData);
                }}
            />
        );
    };

    const renderTaskResult = () => {
        let taskData;
        try {
            taskData = JSON.parse(message.content);
        } catch {
            taskData = { status: 'completed', result: message.content };
        }

        const getStatusColor = (status: string) => {
            switch (status) {
                case 'completed':
                case 'success':
                    return 'text-emerald-400 bg-emerald-600/20';
                case 'failed':
                case 'error':
                    return 'text-red-400 bg-red-600/20';
                case 'pending':
                case 'running':
                    return 'text-yellow-400 bg-yellow-600/20';
                default:
                    return 'text-slate-400 bg-slate-600/20';
            }
        };

        return (
            <div
                className={`border ${colors.border} rounded-lg overflow-hidden ${colors.messageBg}`}
            >
                <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                            <span className="font-semibold text-base">Task Result</span>
                            <span
                                className={`px-2 py-1 text-xs rounded ${getStatusColor(taskData.status)}`}
                            >
                                {taskData.status || 'completed'}
                            </span>
                        </div>
                        <div className="flex items-center space-x-1">
                            <motion.button
                                onClick={handleCopy}
                                className="p-1 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/50"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Copy className="w-3 h-3" />
                            </motion.button>
                            <motion.button
                                className="p-1 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/50"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Download className="w-3 h-3" />
                            </motion.button>
                        </div>
                    </div>

                    {taskData.summary && (
                        <p className="text-base text-slate-300 mb-2 leading-relaxed">
                            {taskData.summary}
                        </p>
                    )}

                    {taskData.result && (
                        <div className="bg-slate-800/30 rounded p-2 text-base leading-relaxed">
                            <pre className="whitespace-pre-wrap">
                                {typeof taskData.result === 'string'
                                    ? taskData.result
                                    : JSON.stringify(taskData.result, null, 2)}
                            </pre>
                        </div>
                    )}

                    {taskData.files && taskData.files.length > 0 && (
                        <div className="mt-2">
                            <p className="text-sm text-slate-400 mb-1">Generated Files:</p>
                            <div className="space-y-1">
                                {taskData.files.map(
                                    (
                                        file: { path?: string; size?: string } | string,
                                        index: number
                                    ) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between text-sm bg-slate-800/30 rounded p-1"
                                        >
                                            <span className="font-mono">
                                                {typeof file === 'string' ? file : file.path || ''}
                                            </span>
                                            <span className="text-slate-400">
                                                {typeof file === 'string'
                                                    ? 'N/A'
                                                    : file.size || 'N/A'}
                                            </span>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {(taskData.status === 'failed' || message.relatedTaskId) && (
                        <div className="flex items-center space-x-2 mt-3">
                            {taskData.status === 'failed' && (
                                <motion.button
                                    onClick={() =>
                                        handleActionClick({ label: 'Retry' }, { data: taskData })
                                    }
                                    className="px-2 py-1 text-sm bg-orange-600/20 text-orange-300 rounded hover:bg-orange-600/30"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Retry
                                </motion.button>
                            )}
                            {message.relatedTaskId && (
                                <motion.button
                                    onClick={() =>
                                        handleActionClick({ label: 'Recall' }, { data: taskData })
                                    }
                                    className="px-2 py-1 text-sm bg-emerald-600/20 text-emerald-300 rounded hover:bg-emerald-600/30"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Recall
                                </motion.button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    switch (message.type) {
        case 'code':
            return renderCodeMessage();
        case 'pluginCard':
            return renderPluginCard();
        case 'taskResult':
            return renderTaskResult();
        case 'run-log': {
            // Run logs render as inline statements, not cards
            // This provides cleaner presentation as organized statements before/after cards
            return (
                <RunLogStatement
                    content={message.content}
                    timestamp={new Date(message.timestamp)}
                />
            );
        }
        default:
            return (
                <div className="p-3 rounded-xl bg-muted/20 border border-muted">
                    <p className="text-base whitespace-pre-wrap text-gray-100 leading-relaxed">
                        {message.content}
                    </p>
                </div>
            );
    }
};

export default MessageCard;
export { MessageCard };
